import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { BusinessUnit } from '@/components/finance/BusinessUnitSelector';

export interface BankAccount {
  id: string;
  name: string;
  bank_name: string | null;
  account_number_last4: string | null;
  business_unit: string;
  currency: string;
  initial_balance: number | null;
  current_balance: number | null;
  is_active: boolean | null;
  is_default: boolean | null;
  metadata: any;
  created_at: string | null;
  updated_at: string | null;
}

interface CreateBankAccountData {
  name: string;
  bank_name?: string;
  account_number_last4?: string;
  business_unit: string;
  currency: string;
  initial_balance?: number;
  is_default?: boolean;
}

export function useBankAccounts(businessUnit: BusinessUnit = 'all') {
  return useQuery({
    queryKey: ['bank-accounts', businessUnit],
    queryFn: async () => {
      let query = supabase
        .from('bank_accounts')
        .select('*')
        .eq('is_active', true)
        .order('name');
      
      if (businessUnit !== 'all') {
        query = query.eq('business_unit', businessUnit);
      }
      
      const { data, error } = await query;
      
      if (error) throw error;
      return data as BankAccount[];
    },
  });
}

export function useCreateBankAccount() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: CreateBankAccountData) => {
      const { data: result, error } = await supabase
        .from('bank_accounts')
        .insert({
          ...data,
          current_balance: data.initial_balance || 0,
        })
        .select()
        .single();
      
      if (error) throw error;
      return result as BankAccount;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bank-accounts'] });
    },
  });
}

export function useUpdateBankAccount() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, ...data }: Partial<BankAccount> & { id: string }) => {
      const { data: result, error } = await supabase
        .from('bank_accounts')
        .update(data)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return result as BankAccount;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bank-accounts'] });
    },
  });
}
